import React, { useEffect, useState, useMemo } from "react";
import { GITHUB_CONTRIBUTION_GRAPH_THEME } from "../constants/github";

const API = (username) =>
  `https://github-contributions-api.jogruber.de/v4/${encodeURIComponent(
    username
  )}?y=last`;

function parseYMD(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function formatYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Columns = weeks (Sun–Sat rows), matching GitHub’s layout. */
function buildWeeks(contributions) {
  if (!contributions?.length) return [];
  const map = new Map(contributions.map((c) => [c.date, c]));
  const sorted = [...contributions].sort((a, b) => a.date.localeCompare(b.date));
  const first = parseYMD(sorted[0].date);
  const last = parseYMD(sorted[sorted.length - 1].date);

  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());

  const weeks = [];
  const cur = new Date(start);
  let currentWeek = [];

  while (cur <= last) {
    const ymd = formatYMD(cur);
    currentWeek.push(map.get(ymd) || { date: ymd, count: 0, level: 0 });
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
    cur.setDate(cur.getDate() + 1);
  }
  if (currentWeek.length) {
    while (currentWeek.length < 7) {
      currentWeek.push({ date: "", count: 0, level: 0, pad: true });
    }
    weeks.push(currentWeek);
  }
  return weeks;
}

const DEFAULT_THEME = GITHUB_CONTRIBUTION_GRAPH_THEME;

/**
 * Contribution graph using github-contributions-api.jogruber.de (the
 * react-github-calendar package’s bundled Heroku proxy is dead → "Error :(").
 */
function GithubContributionCalendar({
  username,
  blockSize = 12,
  blockMargin = 3,
  theme: themeProp,
  showTotalCount = true,
  className = "",
}) {
  const theme = useMemo(
    () => ({ ...DEFAULT_THEME, ...themeProp }),
    [themeProp]
  );
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setData(null);
    fetch(API(username))
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((json) => {
        if (cancelled) return;
        if (!json?.contributions?.length) {
          throw new Error("No contribution data");
        }
        setData(json);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Failed to load");
      });
    return () => {
      cancelled = true;
    };
  }, [username]);

  const weeks = useMemo(
    () => (data ? buildWeeks(data.contributions) : []),
    [data]
  );

  const textHeight = 0;
  const width =
    weeks.length > 0
      ? weeks.length * (blockSize + blockMargin) - blockMargin
      : 0;
  const height =
    weeks.length > 0 ? 7 * (blockSize + blockMargin) - blockMargin : 0;

  const total =
    data?.total?.lastYear != null ? data.total.lastYear : null;

  if (error) {
    return (
      <p className="github-contribution-calendar-error text-muted small mb-0">
        Couldn&apos;t load GitHub activity.{" "}
        <a
          href={`https://github.com/${username}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          View profile on GitHub
        </a>
      </p>
    );
  }

  if (!data || !weeks.length) {
    return (
      <div className="github-contribution-calendar-loading small">
        Loading…
      </div>
    );
  }

  return (
    <article className={`github-contribution-calendar ${className}`.trim()}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="github-contribution-calendar-svg"
        style={{ backgroundColor: theme.background }}
        aria-hidden={true}
      >
        {weeks.map((week, x) => (
          <g
            key={x}
            transform={`translate(${x * (blockSize + blockMargin)}, 0)`}
          >
            {week.map((day, y) => {
              const lvl = day.pad
                ? 0
                : Math.min(4, Math.max(0, day.level ?? 0));
              return (
                <rect
                  key={day.date || `pad-${x}-${y}`}
                  x={0}
                  y={textHeight + y * (blockSize + blockMargin)}
                  width={blockSize}
                  height={blockSize}
                  fill={theme[`grade${lvl}`]}
                  rx={1}
                  opacity={day.pad ? 0.25 : 1}
                >
                  {!day.pad && day.date ? (
                    <title>
                      {`${day.count ?? 0} contributions on ${day.date}`}
                    </title>
                  ) : null}
                </rect>
              );
            })}
          </g>
        ))}
      </svg>
      {showTotalCount && total != null && (
        <div className="github-contribution-calendar-meta">
          <span>{total} contributions in the last year</span>
        </div>
      )}
    </article>
  );
}

export default GithubContributionCalendar;
